import { useState } from 'preact/hooks';
import { authService, UserInfo } from '../functions/auth/authService';
import { googleAuthService } from '../functions/auth/googleAuthService';
import { microsoftAuthService } from '../functions/auth/microsoftAuthService';
import '../css/login.css';

interface LoginProps {
  onLoginSuccess: (userInfo: UserInfo) => void;
}

const Login = ({ onLoginSuccess }: LoginProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'quick' | 'email'>('quick');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');
    try {
      const userInfo = await googleAuthService.signInWithGoogle();
      onLoginSuccess(userInfo);
    } catch (err) {
      console.error('Google login error:', err);
      setError(err instanceof Error ? err.message : 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setIsLoading(true);
    setError('');
    try {
      const userInfo = await microsoftAuthService.signInWithMicrosoft();
      onLoginSuccess(userInfo);
    } catch (err) {
      console.error('Microsoft login error:', err);
      setError(err instanceof Error ? err.message : 'Microsoft login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e: Event) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userInfo = await authService.loginWithEmailPassword({ email, password });
      onLoginSuccess(userInfo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Background Overlay */}
      <div style={styles.overlay} />
      
      {/* Content Wrapper with padding */}
      <div style={styles.contentWrapper}>
        {/* Logo at top */}
        <div style={styles.logoContainer}>
          <img 
            src="/images/LOGO.png" 
            alt="Fractal Investors" 
            style={styles.logo}
          />
        </div>
        
        {/* Login Card */}
        <div style={styles.loginCard}>
        {/* Welcome Text */}
        <div style={styles.textContainer}>
          <h1 style={styles.welcomeText}>Welcome</h1>
          <h2 style={styles.signInText}>Sign in</h2>
        </div>
        
        {/* Tab Navigation */}
        <div style={styles.tabContainer}>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === 'quick' ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab('quick')}
          >
            Quick Login
          </button>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === 'email' ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab('email')}
          >
            Email Login
          </button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'quick' ? (
          <div style={styles.quickLoginContainer}>
            {/* Error Message */}
            {error && <div style={styles.errorMessage}>{error}</div>}
            
            {/* OAuth Buttons - Side by Side */}
            <div style={styles.oauthButtonsContainer}>
              <button 
                style={styles.googleButton}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                <div style={styles.googleIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <span style={styles.buttonText}>Google</span>
              </button>
              
              <button 
                style={styles.microsoftButton}
                onClick={handleMicrosoftSignIn}
                disabled={isLoading}
              >
                <div style={styles.microsoftIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                </div>
                <span style={styles.buttonText}>Microsoft</span>
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.emailLoginContainer}>
            {/* Email/Password Form */}
            <form onSubmit={handleEmailLogin} style={styles.form}>
              {error && <div style={styles.errorMessage}>{error}</div>}
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                  style={styles.input}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                    style={styles.input}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.passwordToggle}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                style={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundImage: 'url(/images/bg-wide2.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    overflow: 'auto',
    margin: 0,
    padding: 0,
  },
  
  contentWrapper: {
    position: 'relative' as const,
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    boxSizing: 'border-box' as const,
    zIndex: 2,
  },
  
  overlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    zIndex: 1,
  },
  
  loginCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
    padding: '40px',
    width: '90%',
    maxWidth: '480px',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    position: 'relative' as const,
    zIndex: 2,
  },
  
  logoContainer: {
    marginBottom: '40px',
    display: 'flex',
    justifyContent: 'center',
    position: 'relative' as const,
    zIndex: 2,
  },
  
  logo: {
    height: '120px',
    width: 'auto',
    objectFit: 'contain' as const,
  },
  
  textContainer: {
    textAlign: 'center' as const,
    marginBottom: '32px',
  },
  
  welcomeText: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  signInText: {
    fontSize: '16px',
    fontWeight: '400',
    color: '#64748b',
    margin: '0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  tabContainer: {
    display: 'flex',
    width: '100%',
    marginBottom: '24px',
    backgroundColor: '#f1f5f9',
    borderRadius: '8px',
    padding: '4px',
  },
  
  tabButton: {
    flex: 1,
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  activeTab: {
    backgroundColor: '#ffffff',
    color: '#1e293b',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  
  quickLoginContainer: {
    width: '100%',
  },
  
  oauthButtonsContainer: {
    width: '100%',
    display: 'flex',
    gap: '12px',
  },
  
  emailLoginContainer: {
    width: '100%',
  },
  
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  
  passwordContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  
  passwordToggle: {
    position: 'absolute' as const,
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
  },
  
  submitButton: {
    width: '100%',
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  errorMessage: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    border: '1px solid #fecaca',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  
  googleButton: {
    flex: 1,
    height: '48px',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  
  microsoftButton: {
    flex: 1,
    height: '48px',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  
  googleIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  microsoftIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  buttonText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

// Responsive styles
const mediaQueries = `
  @media (max-width: 640px) {
    .login-card {
      width: 95% !important;
      padding: 24px !important;
      min-height: 400px !important;
    }
    
    .welcome-text {
      font-size: 24px !important;
    }
    
    .sign-in-text {
      font-size: 14px !important;
    }
    
    .oauth-buttons-container {
      flex-direction: column !important;
      gap: 8px !important;
    }
    
    .google-button,
    .microsoft-button {
      height: 44px !important;
    }
    
    .button-text {
      font-size: 13px !important;
    }
  }
  
  @media (min-width: 641px) and (max-width: 1024px) {
    .login-card {
      width: 80% !important;
      max-width: 400px !important;
    }
  }
  
  .google-button:hover {
    border-color: #4285F4 !important;
    box-shadow: 0 2px 4px rgba(66, 133, 244, 0.15) !important;
    transform: translateY(-1px) !important;
  }
  
  .microsoft-button:hover {
    border-color: #0078d4 !important;
    box-shadow: 0 2px 4px rgba(0, 120, 212, 0.15) !important;
    transform: translateY(-1px) !important;
  }
  
  .google-button:active,
  .microsoft-button:active {
    transform: translateY(0) !important;
  }
  
  .google-button:disabled,
  .microsoft-button:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
  }
  
  .submit-button:hover {
    background-color: #2563eb !important;
  }
  
  .submit-button:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
  }
  
  .input:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = mediaQueries;
  document.head.appendChild(styleSheet);
}

export default Login;
